// frontend/src/utils/preferenceIcons.js

import {
  Globe,
  Calendar,
  Clock,
  Ruler,
  Thermometer,
  Filter,
  Eye,
  HelpCircle,
  Bell,
  Mail,
  MessageSquare,
  Smartphone,
  Palette,
  Languages,
  MapPin,
  Timer,
  Settings
} from 'lucide-react';

// Map preference keys to their corresponding icons
const preferenceIconMap = {
  // Display preferences
  language: Languages,
  theme: Palette,
  date_format: Calendar,
  time_format: Clock,
  show_profile_picture: Eye,
  enable_tooltips: HelpCircle,
  global_filter_threshold: Filter,
  
  // Unit preferences
  distance_unit: Ruler,
  temperature_unit: Thermometer,
  
  // Notification preferences
  email_booking_confirmation: Mail,
  email_booking_reminder: Mail,
  email_trip_updates: Mail,
  email_schedule_changes: Mail,
  sms_booking_confirmation: MessageSquare,
  sms_booking_reminder: MessageSquare,
  sms_trip_updates: MessageSquare,
  sms_schedule_changes: MessageSquare,
  push_booking_confirmation: Smartphone,
  push_booking_reminder: Smartphone,
  push_trip_updates: Smartphone,
  push_schedule_changes: Smartphone,
  reminder_hours_before: Timer,
  quiet_hours_start: Bell,
  quiet_hours_end: Bell,
  
  // Entity preferences
  time_zone: Globe,
  country: MapPin,
  default_launch_page: Settings,
  
  // Default icon for unknown preferences
  default: Settings
};

// Map categories to their icons
const categoryIconMap = {
  display: Palette,
  units: Ruler,
  notifications: Bell,
  organization: Globe,
  entity_attribute: MapPin,  // User's entity preferences
  security: Settings,
  general: Settings
};

/**
 * Get icon component for a preference key
 * @param {string} preferenceKey - The preference key
 * @returns {React.Component} The icon component
 */
export const getPreferenceIcon = (preferenceKey) => {
  return preferenceIconMap[preferenceKey] || preferenceIconMap.default;
};

/**
 * Get icon component for a category
 * @param {string} category - The category name
 * @returns {React.Component} The icon component
 */
export const getCategoryIcon = (category) => {
  return categoryIconMap[category] || categoryIconMap.general;
};

/**
 * Map data type to input type for HTML inputs
 * @param {string} dataType - The data type from preference definition
 * @returns {string} The HTML input type
 */
export const getInputType = (dataType) => {
  switch (dataType) {
    case 'integer':
      return 'number';
    case 'boolean':
      return 'checkbox';
    case 'string':
    default:
      return 'text';
  }
};

/**
 * Get translation key for a category
 * @param {string} category - The category key
 * @returns {string} The translation key (not the translated text)
 */
export const getCategoryDisplayName = (category) => {
  // Return translation keys that match translation.json
  const translationKeys = {
    display: 'displaySettings',
    units: 'unitsMeasurements',  // Matches existing key in translation.json
    notifications: 'notificationPreferences',
    organization: 'organizationSettings',  // For admin Organization Settings page
    entity_attribute: 'entityAttributes',  // User's entity preferences (is_primary, etc.)
    security: 'security',
    general: 'general'
  };

  return translationKeys[category] || category;
};